import {Component, EventEmitter, Input, OnInit} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {FormModel} from "../form/form.model.abstract";
import {FormConfig} from "../form/form.config";
import {AdvisorComponent} from "../advisor.component";
import {FormModelDates} from '../form/form.model.dates';
import {ValidationService} from '../services/validation.service';

/**
 * Component for showing a single formfield. The type is set by the FormModel configuration and the template decides which html to show based ion the type
 */
@Component({
  selector: '[formfield]',
  templateUrl: './formfield.component.html'
})
export class FormfieldComponent implements OnInit {
  /**
   * The configuration for this formfield
   */
  @Input() formModel: FormModel;

  /**
   * the formgroup for this field to have access on the validation
   */
  @Input() form: UntypedFormGroup;

  /**
   * The configuration of the whole form
   */
  @Input() formConfig: FormConfig;

  /**
   * current step where this form is displayd
   */
  @Input() step: string;

  /**
   * Should there be some infos visible for the joyride tour
   */
  @Input() tourInfo: boolean = false;

  /**
   * toggle to decide if a field is invalid when it's empty
   */
  @Input() invalidIfEmpty: boolean = true;

  /**
   * toggle for showing the help as inline element or not.
   */
  @Input() inlineHelp: boolean = false;

  /**
   * toggle for showing/hiding the inline help
   */
  @Input() showInlineHelp: boolean = false;

  /**
   * is used to save focus marker for specific field ids. Can be used in the template to add special classes.
   */
  focus: {string: boolean}[] = [];

  /**
   * list of (sub)field ids that are invalid.
   */
  invalid: {string: boolean}[] = [];

  /**
   * Eventemitter that is called on the first focus on the element.
   */
  firstFocus: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Eventemitter that is called on every focus on the element.
   */
  everyFocus: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Eventhandler that can be triggered to close the infowindow
   */
  closeInfoWindow: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Holds a textkey of a teasertext if there should be one shown
   */
  teaserTextkey: string = null;

  /**
   * toggle for showing a hint text on an input field.
   */
  hasInputHint: boolean = false;

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // check if there should be a teasertext visible
    if(this.formModel.hasTeaser === true) {
      this.teaserTextkey = this.getLabel().replace(/\.label/, '.teaser');
    }
  }

  /**
   * Returns the label textkey for a field
   */
  getLabel(id: string = null): string {
    if(id === null) {
      id = this.formModel.id;
    }

    if(this.formModel.idPostfix) {
      // remove the idprefix to use the same texts as in the original field.
      id = id.replace(new RegExp(this.formModel.idPostfix + "$"), "");
    }

    return this.formConfig.getLabel(this.step, id);
  }

  /**
   * Returns the label textkey for a field
   */
  getExtraLabelText(): string {
    if (this.formModel.isRequired() === false && this.formModel.type !== 'checkboxes') {
      return 'formfield.optional';
    }
    return null;
  }

  /**
   * Returns the textkey for the error messages.
   */
  getError(): string {
    const errorId = this.formModel.errorId? this.formModel.errorId : this.formModel.id;

    if(this.isWarning()) {
      return this.step + "." + errorId + ".warning";
    }
    return this.step + "." + errorId + ".error";
  }

  /**
   * Returns some placeholds for the error messages.
   */
  getErrorPlaceholder(): any {
    let placeholders = {fallback: this.isWarning() ? 'form.warning.generic' : 'form.error.generic'};
    this.formModel.getIds().forEach(id => {
      placeholders = {...placeholders, ...this.form.controls[id].errors};
    });
    return placeholders;
  }

  /**
   * Checks if the current field is invalid or not.
   */
  isInvalid(): boolean {
    return this.formModel.getIds().filter(id => {
      return this.form.controls[id].touched && this.form.controls[id].invalid && (this.invalidIfEmpty === true || this.getValue(id)?.length > 0);
    }).length > 0;
  }

  isInvalidFirstField(): boolean {
    const ids = this.formModel.getIds();
    if (typeof ids[0] === 'string') {
      return this.form.controls[ids[0]].touched && this.form.controls[ids[0]].invalid
        && (this.invalidIfEmpty === true || this.getValue(ids[0])?.length > 0);
    }
    return false;
  }

  isInvalidSecondField(): boolean {
    const ids = this.formModel.getIds();
    if (typeof ids[1] === 'string') {
      return this.form.controls[ids[1]].touched && this.form.controls[ids[1]].invalid
        && (this.invalidIfEmpty === true || this.getValue(ids[1])?.length > 0);
    }
    return false;
  }

  /**
   * .
   */
  isWarning(): boolean {
    return this.formModel.getIds().filter(id => {
      return this.form.controls[id].touched && this.form.controls[id].invalid && this.form.controls[id].errors.hasOwnProperty('serverError');
    }).length > 0;
  }

  /**
   * Returns the textkey for an option of radios and slects
   * @param option value of the option for the label
   */
  getOptionLabel(option: string): string {
    if(option === "") {
      return 'select.pleaseselect';
    }

    let id = this.formModel.id;
    if(this.formModel.idPostfix) {
      // remove the idprefix to use the same texts as in the original field.
      id = id.replace(new RegExp(this.formModel.idPostfix + "$"), "");
    }

    return this.step + "." + id + "." + option.toString().toLowerCase() + ".label" + this.formConfig.getLabelPostfix();
  }

  /**
   * Count the current value one up
   */
  increaseValue(): void {
    this.form.controls[this.formModel.id].setValue(parseInt(this.form.controls[this.formModel.id].value) + 1);
    if(this.form.controls[this.formModel.id].errors?.max) {
      // new value is not valid, reset to the last one
      this.form.controls[this.formModel.id].setValue(this.form.controls[this.formModel.id].errors.max["max"]);
    }
  }

  /**
   * Count the current value one down
   */
  decreaseValue(): void {
    this.form.controls[this.formModel.id].setValue(parseInt(this.form.controls[this.formModel.id].value) - 1);
    // check if the new value is valid
    if(this.form.controls[this.formModel.id].errors?.min) {
      // new value is not valid, reset to the last one
      this.form.controls[this.formModel.id].setValue(this.form.controls[this.formModel.id].errors.min["min"]);
    }
  }

  /**
   * Sets the value of the current field or another id to the form.
   * @param value
   * @param id
   */
  setValue(value: any, id: string = null): void {
    if(id === null) {
      id = this.formModel.id;
    }

    this.form.controls[id].setValue(value);
  }

  /**
   * returns the current value of the field or anothr id from the form element.
   * @param id
   */
  getValue(id: string = null): any {
    if(id === null) {
      id = this.formModel.id;
    }

    return this.form.controls[id].value;
  }

  /**
   * Sets the focus marker on a specific field or the current field.
   * @param id
   */
  onFocus(id?: string): void {
    if(this.formModel.fieldData["readonly"]) {
      // do nothing for readonly fields
      return;
    }

    this.focus[id? id : this.formModel.id] = true;

    // emmit this on every focus
    if(window.screen.width >= AdvisorComponent.SCREEN_WIDTH_MEDIUM) {
      this.everyFocus.emit(false);
    }

    // only emit the first focus once
    if(this.firstFocus) {
      this.firstFocus.emit();
      this.firstFocus = null;
    }
  }

  /**
   * removes the focus marker on a specific field or the current field.
   * @param id
   */
  onBlur(id?: string): void {
    // update focus id
    this.focus[id ? id : this.formModel.id] = false;

    if(this.formModel.type === 'dates') {
      // check if we have two diget year value
      [0, 1].forEach(index => {
        const value = this.form.controls[this.formModel.fieldData['ids'][index]]?.value ?? null;
        const dateParts = value?.match(/^\d{1,2}\.\d{1,2}\.(\d{2})$/);
        if(value && dateParts?.length > 1) {
          const currentYear = parseFloat((new Date()).getFullYear().toString().slice(-2));
          const year = parseFloat(dateParts[1]) <= currentYear ? "20" : "19";
          const newValue = dateParts[0].slice(0, -2) + year + dateParts[1];
          this.form.controls[this.formModel.fieldData['ids'][index]].setValue(newValue);
        }
      });
    }
  }

  /**
   * Eventhandler for clicks on the slider
   */
  onSliderClick(): void {
    this.closeInfoWindow.emit();
  }

  /**
   * Preset the second date field if the preset is activated and the first date field is blured.
   */
  presetFields(): void {
    if(this.formModel.type === 'dates') {
      // check if we should preset and the second value is empty
      const secondValue = this.form.controls[this.formModel.fieldData["ids"][1]]?.value ?? '';
      if((this.formModel as FormModelDates).presetSecondFieldYears > 0 && secondValue.length === 0) {
        // read the date value from the first field
        const dateField = ValidationService.dateFromString(this.form.controls[this.formModel.fieldData["ids"][0]]?.value);

        // check if we could parse the year
        if(dateField) {
          // add number of years configured and remove one day
          dateField.setFullYear(dateField.getFullYear() + (this.formModel as FormModelDates).presetSecondFieldYears);
          dateField.setDate(dateField.getDate() - 1);
          // set new calculated date to the second field
          this.form.controls[this.formModel.fieldData["ids"][1]].setValue(dateField.toLocaleDateString('de-DE'), {onlySelf: true});
        }
      }
    }
  }

  /**
   * Checks the input when pasting someting into a number field and try to replace dot with comma.
   * @param event
   */
  onPasteCheckNumber(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (!clipboardData) {
      return;
    }
    const pastedInput: string = clipboardData.getData('text');

    // if the user has a dot inside a string and copies this into a number field, show hint text
    if (pastedInput.includes('.')) {
      this.showInputHint();
    }

    if (pastedInput.includes(' ')) {
      event.preventDefault();
      const inputElement = event.target as HTMLInputElement;
      inputElement.value = pastedInput.replace(/ /g, '');
    }
  }

  /**
   * Eventhandler for keyboard input on number fields.
   * checks for usages of dots and tries to rplace them with comma.
   *
   * @param event
   */
  onKeyUpCheckNumber(event: KeyboardEvent): void {
    // do not allow the usage of dot. This could lead to wring numbers
    if (event.key === '.') {
      this.showInputHint();
      return;
    }

    const inputElement = event.target as HTMLInputElement;
    if (!inputElement.value.includes('.')) {
      this.hideInputHint();
    }
  }

  /**
   * show the input hint
   */
  showInputHint(): void {
    this.hasInputHint = true;
  }

  /**
   * hide the input hint with some timeout
   */
  hideInputHint(): void {
    this.hasInputHint = false;
  }

  /**
   * Returns the head label textkey for a field group
   */
  getFieldGroupLabel(id: string = null): string {
    if(id === null) {
      id = this.formModel.id;
    }
    return this.step + "." + id + ".group.label";
  }

  /**
   * gets the label value above the slider thumb
   *
   * @param value
   */
  formatThumbLabel(value: number): string {
      return value.toString();
  }

}
